import React, { useState, useMemo } from "react"
import { graphql, Link } from "gatsby"
import styled from "styled-components"
import PortableText from "@sanity/block-content-to-react"
import Layout from "../components/layout"
import SEO from "../components/seo"
import * as variable from "../components/variables"

const PropertyStyle = styled.div`
  .hero-section {
    position: relative;
    height: 500px;
    background-color: #1a1a2e;
    overflow: hidden;

    @media (max-width: ${variable.mobileWidth}) {
      height: 350px;
    }
  }

  .hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.85;
  }

  .hero-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    padding: 60px 0 40px;
  }

  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    color: white;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 14px;

    a {
      color: rgba(255, 255, 255, 0.8);
      text-decoration: none;

      &:hover {
        color: white;
      }
    }

    span {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .hero-title {
    font-size: 48px;
    font-weight: 700;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;

    @media (max-width: ${variable.mobileWidth}) {
      font-size: 32px;
    }
  }

  .hero-tagline {
    font-size: 20px;
    opacity: 0.9;
    margin: 0;

    @media (max-width: ${variable.mobileWidth}) {
      font-size: 16px;
    }
  }

  .main-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 60px 20px;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 50px;

    @media (max-width: ${variable.tabletWidth}) {
      grid-template-columns: 1fr;
    }
  }

  .left-column {
    min-width: 0;
  }

  .right-column {
    @media (max-width: ${variable.tabletWidth}) {
      order: -1;
    }
  }

  .section-title {
    font-size: 28px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0 0 24px 0;
    display: flex;
    align-items: center;
    gap: 16px;

    &::after {
      content: "";
      flex: 1;
      height: 2px;
      background: linear-gradient(90deg, ${variable.blue}, transparent);
    }
  }

  .description-section {
    margin-bottom: 50px;

    p {
      font-size: 16px;
      line-height: 1.8;
      color: #4a5568;
      margin-bottom: 16px;
    }

    ul, ol {
      margin-bottom: 16px;
      padding-left: 24px;
    }

    li {
      font-size: 16px;
      line-height: 1.8;
      color: #4a5568;
      margin-bottom: 8px;
    }
  }

  .amenities-section {
    margin-bottom: 50px;

    ul {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      padding: 0;
      margin: 0;
      list-style: none;

      @media (max-width: ${variable.mobileWidth}) {
        grid-template-columns: 1fr;
      }
    }

    li {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 15px;
      color: #4a5568;
      padding: 12px 16px;
      background: #f8f9fa;
      border-radius: 8px;

      &::before {
        content: "✓";
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background: ${variable.blue};
        color: white;
        border-radius: 50%;
        font-size: 12px;
        font-weight: bold;
        flex-shrink: 0;
      }
    }

    p {
      font-size: 16px;
      line-height: 1.8;
      color: #4a5568;
    }
  }

  .gallery-section {
    margin-bottom: 50px;
  }

  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: ${variable.mobileWidth}) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .gallery-item {
    aspect-ratio: 4/3;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .contact-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    padding: 32px;
    position: sticky;
    top: 100px;
  }

  .contact-card-title {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0 0 8px 0;
  }

  .contact-card-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 24px 0;
  }

  .contact-info {
    margin-bottom: 24px;
  }

  .contact-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .contact-icon {
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, ${variable.blue}, ${variable.red});
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }

  .contact-details {
    flex: 1;

    .label {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #9ca3af;
      margin-bottom: 4px;
    }

    .value {
      font-size: 15px;
      color: #1a1a2e;
      line-height: 1.4;
    }

    a {
      color: ${variable.blue};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .cta-button {
    display: block;
    width: 100%;
    padding: 16px 24px;
    background: linear-gradient(135deg, ${variable.red}, #7a0129);
    color: white;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    text-decoration: none;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(163, 1, 60, 0.3);
    }
  }

  .neighborhood-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: ${variable.blue};
    color: white;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 24px;
  }

  .lightbox {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 8px;
    }
  }

  .lightbox-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 24px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 20px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    &.prev {
      left: 20px;
    }

    &.next {
      right: 20px;
    }
  }
`

const serializers = {
  types: {
    block: props => {
      const style = props.node.style || "normal"
      if (style === "normal") {
        return <p>{props.children}</p>
      }
      if (style === "h2") {
        return <h2>{props.children}</h2>
      }
      if (style === "h3") {
        return <h3>{props.children}</h3>
      }
      return <p>{props.children}</p>
    },
  },
  list: props => {
    const { type } = props
    if (type === "bullet") {
      return <ul>{props.children}</ul>
    }
    return <ol>{props.children}</ol>
  },
  listItem: props => <li>{props.children}</li>,
}

export const query = graphql`
  query PropertyByID($id: String!) {
    site {
      siteMetadata {
        title
        url
      }
    }
    property: sanityProperty(id: { eq: $id }) {
      headline
      tagline
      address
      slug {
        current
      }
      main_image {
        asset {
          url
        }
      }
      images {
        asset {
          url
        }
      }
      neighborhood {
        headline
        slug {
          current
        }
      }
      fields {
        geocodedLat
        geocodedLng
      }
      _rawDescription
      _rawAmenities
    }
  }
`

const PropertyTemplate = ({ data }) => {
  const property = data.property
  const siteUrl = data.site.siteMetadata.url || ""
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Combine main image with gallery images
  const allImages = [
    property.main_image?.asset?.url,
    ...(property.images || []).map(img => img?.asset?.url),
  ].filter(Boolean)

  // Extract plain text from portable text for SEO description
  const getPlainText = rawContent => {
    if (!rawContent || !Array.isArray(rawContent)) return ""
    return rawContent
      .filter(block => block._type === "block")
      .map(block => {
        if (block.children) {
          return block.children
            .filter(child => child._type === "span")
            .map(span => span.text)
            .join("")
        }
        return ""
      })
      .join(" ")
      .trim()
  }

  // Generate SEO description
  const seoDescription = useMemo(() => {
    const descText = getPlainText(property._rawDescription)
    const neighborhood = property.neighborhood?.headline
    const baseDesc = property.tagline
      ? `${property.tagline}. `
      : ""
    const locationDesc = neighborhood
      ? `Located in ${neighborhood}, Kansas City. `
      : "Kansas City corporate housing. "
    const contentDesc = descText
      ? descText.substring(0, 120) + (descText.length > 120 ? "..." : "")
      : "Fully furnished corporate apartment with premium amenities."
    return `${baseDesc}${locationDesc}${contentDesc}`
  }, [property])

  // Generate JSON-LD structured data for real estate listing
  const structuredData = useMemo(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Accommodation",
      name: property.headline,
      description: seoDescription,
      url: `${siteUrl}/property/${property.slug?.current}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: property.address,
        addressLocality: "Kansas City",
        addressRegion: "MO",
        addressCountry: "US",
      },
      ...(property.main_image?.asset?.url && {
        image: [
          `${property.main_image.asset.url}?w=1200&h=630&fit=crop`,
          `${property.main_image.asset.url}?w=800&h=800&fit=crop`,
          `${property.main_image.asset.url}?w=1200&h=900&fit=crop`,
        ],
      }),
      ...(property.fields?.geocodedLat &&
        property.fields?.geocodedLng && {
          geo: {
            "@type": "GeoCoordinates",
            latitude: property.fields.geocodedLat,
            longitude: property.fields.geocodedLng,
          },
        }),
      ...(property.neighborhood?.headline && {
        containedInPlace: {
          "@type": "Neighborhood",
          name: property.neighborhood.headline,
        },
      }),
      amenityFeature: getPlainText(property._rawAmenities)
        ? getPlainText(property._rawAmenities)
            .split(/[,.\n]/)
            .filter(a => a.trim())
            .slice(0, 10)
            .map(amenity => ({
              "@type": "LocationFeatureSpecification",
              name: amenity.trim(),
            }))
        : undefined,
      provider: {
        "@type": "Organization",
        name: "Kansas City Corporate Housing",
        url: siteUrl,
        telephone: "+1-844-816-1218",
        email: "info@kansascitycorporatehousing.com",
      },
    }

    // Add breadcrumb schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Properties",
          item: `${siteUrl}/locations`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: property.headline,
          item: `${siteUrl}/property/${property.slug?.current}`,
        },
      ],
    }

    return [schema, breadcrumbSchema]
  }, [property, siteUrl, seoDescription])

  const openLightbox = index => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  const nextImage = () => {
    setLightboxIndex(prev => (prev + 1) % allImages.length)
  }

  const prevImage = () => {
    setLightboxIndex(prev => (prev - 1 + allImages.length) % allImages.length)
  }

  return (
    <Layout>
      <SEO
        title={property.headline}
        description={seoDescription}
        image={property.main_image?.asset?.url}
        url={`/property/${property.slug?.current}`}
        type="place"
        structuredData={structuredData}
      />

      <PropertyStyle>
        {/* Hero Section */}
        <div className="hero-section">
          {property.main_image?.asset?.url && (
            <img
              src={`${property.main_image.asset.url}?w=1920&h=500&fit=crop`}
              alt={property.headline}
              className="hero-image"
            />
          )}
          <div className="hero-overlay">
            <div className="hero-content">
              <div className="breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <span>Properties</span>
                <span>/</span>
                <span>{property.headline}</span>
              </div>
              <h1 className="hero-title">{property.headline}</h1>
              {property.tagline && (
                <p className="hero-tagline">{property.tagline}</p>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="content-grid">
            {/* Left Column */}
            <div className="left-column">
              {property.neighborhood && (
                <div className="neighborhood-badge">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {property.neighborhood.headline}
                </div>
              )}

              {/* Description */}
              {property._rawDescription && (
                <div className="description-section">
                  <h2 className="section-title">About This Property</h2>
                  <PortableText
                    blocks={property._rawDescription}
                    serializers={serializers}
                  />
                </div>
              )}

              {/* Amenities */}
              {property._rawAmenities && (
                <div className="amenities-section">
                  <h2 className="section-title">Amenities & Features</h2>
                  <PortableText
                    blocks={property._rawAmenities}
                    serializers={serializers}
                  />
                </div>
              )}

              {/* Gallery */}
              {allImages.length > 1 && (
                <div className="gallery-section">
                  <h2 className="section-title">Photo Gallery</h2>
                  <div className="gallery-grid">
                    {allImages.slice(1).map((url, index) => (
                      <div
                        key={index}
                        className="gallery-item"
                        onClick={() => openLightbox(index + 1)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={e =>
                          e.key === "Enter" && openLightbox(index + 1)
                        }
                      >
                        <img
                          src={`${url}?w=400&h=300&fit=crop`}
                          alt={`${property.headline} - Image ${index + 2}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Right Column - Contact Card */}
            <div className="right-column">
              <div className="contact-card">
                <h3 className="contact-card-title">Interested in this property?</h3>
                <p className="contact-card-subtitle">
                  Contact us today to schedule a tour or learn more
                </p>

                <div className="contact-info">
                  <div className="contact-item">
                    <div className="contact-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div className="contact-details">
                      <div className="label">Address</div>
                      <div className="value">{property.address}</div>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div className="contact-details">
                      <div className="label">Phone</div>
                      <div className="value">
                        <a href="tel:+18448161218">(844) 816-1218</a>
                      </div>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                    <div className="contact-details">
                      <div className="label">Email</div>
                      <div className="value">
                        <a href="mailto:info@kansascitycorporatehousing.com">
                          info@kansascitycorporatehousing.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <Link to="/contact" className="cta-button">
                  Contact Us Today
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <div className="lightbox" onClick={closeLightbox}>
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>
            {allImages.length > 1 && (
              <>
                <button
                  className="lightbox-nav prev"
                  onClick={e => {
                    e.stopPropagation()
                    prevImage()
                  }}
                >
                  ‹
                </button>
                <button
                  className="lightbox-nav next"
                  onClick={e => {
                    e.stopPropagation()
                    nextImage()
                  }}
                >
                  ›
                </button>
              </>
            )}
            <img
              src={`${allImages[lightboxIndex]}?w=1200`}
              alt={`${property.headline} - Image ${lightboxIndex + 1}`}
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
      </PropertyStyle>
    </Layout>
  )
}

export default PropertyTemplate
